import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { contentKindLabel } from './buildContentLibrary';
import { getMediaPreviewPlayUrl } from './mediaPreview';

type MediaPreviewLightboxProps = {
  asset: MediaAsset;
  onClose: () => void;
};

/**
 * Full-size media preview overlay — Escape / backdrop / Close dismisses.
 */
export function MediaPreviewLightbox({ asset, onClose }: MediaPreviewLightboxProps) {
  const { t } = useTranslation(['content', 'common']);
  const url = getMediaPreviewPlayUrl(asset);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  if (!url) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('previewLabel', { name: asset.originalFilename })}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-8"
      style={{ background: 'color-mix(in srgb, #0a0a0b 72%, transparent)' }}
      onClick={onClose}
    >
      <div
        className="relative flex max-h-full w-full max-w-5xl flex-col gap-3"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="m-0 truncate text-[15px] font-semibold text-white">
              {asset.originalFilename}
            </p>
            <p className="m-0 text-[12.5px] text-white/70">{contentKindLabel(asset.kind)}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-[10px] border border-white/20 bg-white/10 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-white/20"
          >
            {t('common:actions.close')}
          </button>
        </div>

        <div
          className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[14px]"
          style={{ background: '#0a0a0b' }}
        >
          {asset.kind === 'video' ? (
            <video
              src={url}
              controls
              autoPlay
              className="max-h-[min(80vh,900px)] max-w-full"
            />
          ) : (
            <img
              src={url}
              alt={asset.originalFilename}
              className="max-h-[min(80vh,900px)] max-w-full object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
}
